import {
  type FrameBuilderExtraService,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutColor,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderSpacerColor,
  type FrameBuilderSpacerSize,
  type FrameBuilderState,
  type FrameBuilderTexture,
} from './state'

interface UpdateArtworkSizeAction {
  type: 'artworkSize'
  width?: number
  height?: number
}

interface UpdateArtworkImageAction {
  type: 'artworkImage'
  url?: string
  name?: string
  color?: string
}

interface UpdateFrameStyleAction {
  type: 'frameStyle'
  profile?: FrameBuilderProfile
  texture?: FrameBuilderTexture
}

interface UpdatePassepartoutAction {
  type: 'passepartout'
  size?: FrameBuilderPassepartoutSize
  defaultColor?: FrameBuilderPassepartoutColor
  color?: FrameBuilderPassepartoutColor
}

interface UpdateGlassAction {
  type: 'glass'
  glass?: FrameBuilderGlass
}

interface UpdateExtrasAction {
  type: 'extras'
  spacerSize?: FrameBuilderSpacerSize
  spacerColor?: FrameBuilderSpacerColor
  extraService?: FrameBuilderExtraService
  checked?: boolean
  extraServices?: FrameBuilderExtraService[]
}

export type FrameBuilderStateAction =
  | UpdateArtworkSizeAction
  | UpdateArtworkImageAction
  | UpdateFrameStyleAction
  | UpdatePassepartoutAction
  | UpdateGlassAction
  | UpdateExtrasAction

/**
 * Updates artwork size state.
 */
const updateArtworkSize = (
  state: FrameBuilderState,
  action: UpdateArtworkSizeAction,
) => {
  const partialState: Pick<FrameBuilderState, 'artwork'> = {
    artwork: {
      ...state.artwork,
    },
  }

  if ('width' in action && typeof action.width !== 'undefined') {
    partialState.artwork.width = action.width
  }

  if ('height' in action && typeof action.height !== 'undefined') {
    partialState.artwork.height = action.height
  }

  return partialState
}

/**
 * Updates artwork image state.
 */
const updateArtworkImage = (
  state: FrameBuilderState,
  action: UpdateArtworkImageAction,
) => {
  const partialState: Pick<FrameBuilderState, 'artwork'> = {
    artwork: {
      ...state.artwork,
    },
  }

  if (
    'url' in action &&
    'name' in action &&
    typeof action.url !== 'undefined' &&
    typeof action.name !== 'undefined'
  ) {
    partialState.artwork.image = {
      url: action.url,
      name: action.name,
    }
  }

  if ('color' in action) {
    partialState.artwork.backgroundColor = {
      ...partialState.artwork.backgroundColor,
      current: action.color,
    }
  }

  return partialState
}

/**
 * Updates frame style state.
 */
const updateFrameStyle = (
  state: FrameBuilderState,
  action: UpdateFrameStyleAction,
) => {
  const partialState: Pick<FrameBuilderState, 'texture' | 'profile'> = {}

  if (typeof state.texture !== 'undefined') {
    partialState.texture = state.texture
  }

  if (typeof state.profile !== 'undefined') {
    partialState.profile = state.profile
  }

  if ('texture' in action) {
    partialState.texture = action.texture
  }

  if ('profile' in action) {
    partialState.profile = action.profile
  }

  return partialState
}

/**
 * Updates passepartout state.
 */
const updatePassepartout = (
  state: FrameBuilderState,
  action: UpdatePassepartoutAction,
) => {
  const partialState: Pick<FrameBuilderState, 'passepartout'> = {
    passepartout: {
      ...state.passepartout,
    },
  }

  if ('size' in action) {
    partialState.passepartout.size = action.size
  }

  if ('color' in action) {
    partialState.passepartout.color = action.color
  }

  return partialState
}

/**
 * Updates glass state.
 */
const updateGlass = (state: FrameBuilderState, action: UpdateGlassAction) => {
  const partialState: Pick<FrameBuilderState, 'glass'> = {}

  if (typeof state.glass !== 'undefined') {
    partialState.glass = state.glass
  }

  if ('glass' in action) {
    partialState.glass = action.glass
  }

  return partialState
}

/**
 * Updates extras state.
 */
const updateExtras = (state: FrameBuilderState, action: UpdateExtrasAction) => {
  const partialState: Pick<FrameBuilderState, 'extras'> = {
    extras: {
      ...state.extras,
    },
  }

  if ('extraService' in action && typeof action.extraService !== 'undefined') {
    const extraServices: FrameBuilderExtraService[] = []
    const checked = 'checked' in action ? action.checked : false

    // Clone extra service list and remove unchecked extra service
    state.extras.extraServices?.forEach((extraService) => {
      if (extraService._id === action.extraService?._id && checked) {
        extraServices.push(Object.assign({}, extraService))
      }

      if (extraService._id !== action.extraService?._id) {
        extraServices.push(Object.assign({}, extraService))
      }
    })

    // Add checked extra service
    if (checked) {
      extraServices.push(action.extraService)
    }

    partialState.extras.extraServices = extraServices
  }

  if ('extraServices' in action) {
    // Clone extra service list
    const extraServices: FrameBuilderExtraService[] = []
    action.extraServices?.forEach((extraService) =>
      extraServices.push(Object.assign({}, extraService)),
    )

    partialState.extras.extraServices = extraServices
  }

  if ('spacerSize' in action) {
    partialState.extras.spacer.size = action.spacerSize
  }

  if ('spacerColor' in action) {
    partialState.extras.spacer.color = action.spacerColor
  }

  return partialState
}

/**
 * Updates frame builder state.
 */
export const frameBuilderStateReducer = (
  state: FrameBuilderState,
  action: FrameBuilderStateAction,
): FrameBuilderState => {
  switch (action.type) {
    case 'artworkSize': {
      return {
        ...state,
        ...updateArtworkSize(state, action),
      }
    }

    case 'artworkImage': {
      return {
        ...state,
        ...updateArtworkImage(state, action),
      }
    }

    case 'frameStyle': {
      return {
        ...state,
        ...updateFrameStyle(state, action),
      }
    }

    case 'passepartout': {
      return {
        ...state,
        ...updatePassepartout(state, action),
      }
    }

    case 'glass': {
      return {
        ...state,
        ...updateGlass(state, action),
      }
    }

    case 'extras': {
      return {
        ...state,
        ...updateExtras(state, action),
      }
    }
  }
}
